import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Switch,
  Typography,
  Tooltip,
  Popconfirm,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { sensitiveWordAPI, botAPI, groupAPI } from '../services/api';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

interface SensitiveWordRule {
  id: number;
  bot_id: number | null;
  bot_name?: string;
  group_id: number | null;
  group_title?: string;
  word: string;
  match_type: 'contains' | 'exact' | 'regex';
  action: 'delete' | 'mute' | 'kick';
  mute_duration: number;
  whitelist_enabled: number;
  priority: number;
  status: number;
  description: string | null;
  create_time: string;
  update_time: string;
}

interface BotConfig {
  id: number;
  bot_name: string;
  bot_type: string;
}

interface GroupInfo {
  id: number;
  group_id: number;
  group_title: string;
  bot_id: number;
}

const matchTypeMap: Record<string, { label: string; color: string }> = {
  contains: { label: '包含', color: 'blue' },
  exact: { label: '精确匹配', color: 'green' },
  regex: { label: '正则表达式', color: 'purple' },
};

const actionMap: Record<string, { label: string; color: string }> = {
  delete: { label: '删除消息', color: 'orange' },
  mute: { label: '禁言', color: 'red' },
  kick: { label: '踢出群组', color: 'volcano' },
};

const SensitiveWordRules: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState<SensitiveWordRule[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<SensitiveWordRule | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<{
    botId?: number | null;
    action?: string;
    status?: number;
  }>({});
  const [form] = Form.useForm();

  const actionValue = Form.useWatch('action', form);
  const formBotId = Form.useWatch('botId', form);

  // 获取机器人列表（只显示群管类型）
  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      const gmBots = response.data.filter(
        (b: BotConfig) => b.bot_type === 'group_management'
      );
      setBots(gmBots);
    } catch {
      message.error('获取机器人列表失败');
    }
  };

  // 获取群组列表（根据表单中选择的机器人）
  const fetchGroups = async (botId: number) => {
    try {
      const response = await groupAPI.getAll(botId);
      setGroups(response.data);
    } catch {
      setGroups([]);
    }
  };

  // 获取规则列表
  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await sensitiveWordAPI.getAll(filters);
      setRules(response.data);
    } catch {
      message.error('获取敏感词规则失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  useEffect(() => {
    fetchRules();
  }, [filters]);

  // 当表单中机器人改变时加载对应群组
  useEffect(() => {
    if (formBotId) {
      fetchGroups(formBotId);
    } else {
      setGroups([]);
    }
  }, [formBotId]);

  // 打开新建/编辑弹窗
  const openModal = (rule?: SensitiveWordRule) => {
    if (rule) {
      setEditingRule(rule);
      form.setFieldsValue({
        botId: rule.bot_id,
        word: rule.word,
        matchType: rule.match_type,
        action: rule.action,
        muteDuration: rule.mute_duration,
        whitelistEnabled: !!rule.whitelist_enabled,
        priority: rule.priority,
        groupId: rule.group_id || undefined,
        description: rule.description,
      });
      if (rule.bot_id) {
        fetchGroups(rule.bot_id);
      }
    } else {
      setEditingRule(null);
      form.resetFields();
      form.setFieldsValue({
        matchType: 'contains',
        action: 'delete',
        muteDuration: 60,
        whitelistEnabled: true,
        priority: 0,
      });
      setGroups([]);
    }
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        botId: values.botId || null,
        groupId: values.groupId || null,
        word: values.word,
        matchType: values.matchType,
        action: values.action,
        muteDuration: values.muteDuration,
        whitelistEnabled: values.whitelistEnabled,
        priority: values.priority,
        description: values.description,
      };

      if (editingRule) {
        await sensitiveWordAPI.update(editingRule.id, payload);
        message.success('规则已更新');
      } else {
        await sensitiveWordAPI.create(payload);
        message.success('规则已创建');
      }
      setModalVisible(false);
      fetchRules();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  // 切换状态
  const handleToggleStatus = async (rule: SensitiveWordRule) => {
    try {
      await sensitiveWordAPI.update(rule.id, {
        status: rule.status ? 0 : 1,
      });
      message.success(rule.status ? '已禁用' : '已启用');
      fetchRules();
    } catch {
      message.error('操作失败');
    }
  };

  // 删除
  const handleDelete = async (id: number) => {
    try {
      await sensitiveWordAPI.delete(id);
      message.success('规则已删除');
      fetchRules();
    } catch {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    try {
      await sensitiveWordAPI.batchDelete(selectedRowKeys as number[]);
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchRules();
    } catch {
      message.error('批量删除失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '机器人',
      dataIndex: 'bot_id',
      width: 120,
      render: (botId: number | null, record: SensitiveWordRule) => {
        if (botId === null) return <Tag color="purple">通用</Tag>;
        const bot = bots.find(b => b.id === botId);
        return bot ? bot.bot_name : record.bot_name || `ID:${botId}`;
      },
    },
    {
      title: '关键词/正则',
      dataIndex: 'word',
      width: 200,
      render: (word: string, record: SensitiveWordRule) => (
        <Space direction="vertical" size={0}>
          <Text code>{word}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '匹配方式',
      dataIndex: 'match_type',
      width: 110,
      render: (type: string) => {
        const config = matchTypeMap[type] || { label: type, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '触发动作',
      dataIndex: 'action',
      width: 120,
      render: (action: string, record: SensitiveWordRule) => {
        const config = actionMap[action] || { label: action, color: 'default' };
        return (
          <Space direction="vertical" size={0}>
            <Tag color={config.color}>{config.label}</Tag>
            {action === 'mute' && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.mute_duration > 0 ? `${record.mute_duration} 分钟` : '永久'}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '适用范围',
      width: 150,
      render: (_: any, record: SensitiveWordRule) =>
        record.group_id ? (
          <Tooltip title={`群组 ID: ${record.group_id}`}>
            <Tag color="cyan">{record.group_title || `群组 ${record.group_id}`}</Tag>
          </Tooltip>
        ) : (
          <Tag color="gold">全部群组</Tag>
        ),
    },
    {
      title: '白名单',
      dataIndex: 'whitelist_enabled',
      width: 80,
      render: (enabled: number) =>
        enabled ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            启用
          </Tag>
        ) : (
          <Tag icon={<StopOutlined />} color="default">
            禁用
          </Tag>
        ),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 80,
      sorter: (a: SensitiveWordRule, b: SensitiveWordRule) => a.priority - b.priority,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number, record: SensitiveWordRule) => (
        <Switch
          checked={!!status}
          onChange={() => handleToggleStatus(record)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          size="small"
        />
      ),
    },
    {
      title: '操作',
      width: 140,
      render: (_: any, record: SensitiveWordRule) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此规则吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0, marginBottom: 16 }}>
          敏感词规则管理
        </Title>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select
              placeholder="按机器人筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, botId: value })}
            >
              <Option value={null as any}>通用规则</Option>
              {bots.map((bot) => (
                <Option key={bot.id} value={bot.id}>
                  {bot.bot_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="按动作筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, action: value })}
            >
              <Option value="delete">删除消息</Option>
              <Option value="mute">禁言</Option>
              <Option value="kick">踢出群组</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="按状态筛选"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Col>
        </Row>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchRules}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            添加规则
          </Button>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`确定要删除选中的 ${selectedRowKeys.length} 条规则吗？`}
              onConfirm={handleBatchDelete}
              okText="确定"
              cancelText="取消"
            >
              <Button danger icon={<DeleteOutlined />}>
                批量删除 ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
        />
      </Card>

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingRule ? '编辑敏感词规则' : '添加敏感词规则'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            matchType: 'contains',
            action: 'delete',
            muteDuration: 60,
            whitelistEnabled: true,
            priority: 0,
          }}
        >
          <Form.Item
            label="所属机器人"
            name="botId"
            extra="留空表示通用规则，对所有群管机器人生效"
          >
            <Select placeholder="通用（所有群管机器人）" allowClear>
              {bots.map((bot) => (
                <Option key={bot.id} value={bot.id}>
                  {bot.bot_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="关键词 / 正则表达式"
            name="word"
            rules={[{ required: true, message: '请输入关键词' }]}
            extra="正则模式下输入正则表达式（不需要加 / 分隔符），如：赌.*博|在线.*赌"
          >
            <Input placeholder="输入关键词或正则表达式" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="匹配方式"
                name="matchType"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="contains">包含匹配</Option>
                  <Option value="exact">精确匹配</Option>
                  <Option value="regex">正则匹配</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="触发动作"
                name="action"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="delete">删除消息</Option>
                  <Option value="mute">禁言用户</Option>
                  <Option value="kick">踢出群组</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {actionValue === 'mute' && (
            <Form.Item
              label="禁言时长（分钟）"
              name="muteDuration"
              extra="设为 0 表示永久禁言"
            >
              <InputNumber min={0} max={525600} style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Form.Item
            label="适用群组"
            name="groupId"
            extra={formBotId ? '留空表示对该机器人的所有群组生效' : '请先选择机器人后再选择群组'}
          >
            <Select
              placeholder="全部群组"
              allowClear
              disabled={!formBotId}
            >
              {groups.map((g) => (
                <Option key={g.group_id} value={g.group_id}>
                  {g.group_title} ({g.group_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="白名单机制"
                name="whitelistEnabled"
                valuePropName="checked"
                extra="启用后，操作人的消息不被删除"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="优先级"
                name="priority"
                extra="数值越大越先匹配"
              >
                <InputNumber min={0} max={9999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="规则说明" name="description">
            <TextArea rows={2} placeholder="可选的规则备注" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRule ? '保存修改' : '创建规则'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SensitiveWordRules;
